import { useState, useMemo, useEffect } from 'react';

import { WebView } from '@zenbusiness/application-commons-webview';
import Box from '@zenbusiness/zui/Box';
import Heading from '@zenbusiness/zui/Heading';
import TabContext from '@zenbusiness/zui/TabContext';
import TabPanel from '@zenbusiness/zui/TabPanel';
import PropTypes from 'prop-types';

import PageTitle from '#client/components/PageTitle';
import useGetStatements from '#client/components/StatementsModal/hooks/useGetStatements';
import useBreakpoints from '#client/hooks/useBreakpoints';
import useToastContext from '#client/hooks/useToastContext';
import { formatStatements } from '#client/utils/statements';

import {
  DialogContentStyled,
  TabBoxStyled,
  TabListStyled,
  TabStyled
} from './StatementContent.styles';
import MonthlyStatementsTab from '../MonthlyStatementsTab';
import OtherDocumentsTab from '../OtherDocumentsTab';
import StatementLoading from '../StatementLoading';

const { isWebView } = WebView;

const LOADING_LONG_TIME = 3000;

const STATEMENTS_TAB = '1';
const OTHER_DOCUMENTS_TAB = '2';

export default function StatementContent({ handleClose = null, openOnPage = false }) {
  const [longerLoadTimeFlag, setLongerLoadTimeFlag] = useState(false);
  const { createCommonErrorToast } = useToastContext();
  const {
    sizes: { isMobile }
  } = useBreakpoints();

  const {
    data: dataStatements,
    error,
    handleLoadMore,
    hasNextPagination,
    loading: loadingStatements,
    loadingToken,
    isLoadMore
  } = useGetStatements();

  const [currentTab, setCurrentTab] = useState(STATEMENTS_TAB);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  /**
   * Condition for switching loading type: skeleton or circle
   */
  const isLoadingSkeleton = (loadingStatements || loadingToken) && !isLoadMore;
  const isLoadingCircle = isLoadingSkeleton && longerLoadTimeFlag;

  useEffect(() => {
    if (error) {
      createCommonErrorToast({ error });
      if (typeof handleClose === 'function') {
        handleClose();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  const yearStatements = useMemo(
    () => formatStatements(dataStatements?.statementListItems?.data ?? []),
    [dataStatements]
  );

  const isEmpty =
    !loadingStatements && (!Array.isArray(yearStatements) || yearStatements.length === 0);

  useEffect(() => {
    /**
     * When the loading time is longer than 3 seconds, turn on loading long time flag
     */
    const timer = setTimeout(() => {
      setLongerLoadTimeFlag(true);
    }, LOADING_LONG_TIME);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  return (
    <DialogContentStyled>
      <Box>
        {!isWebView && (
          <>
            {openOnPage ? (
              <PageTitle
                appPageName="statements_screen"
                marginBottom={isMobile ? '16px' : '24px'}
                title="Statements & Documents"
              />
            ) : (
              <Box marginBottom={isMobile ? '16px' : '24px'}>
                <Heading
                  size="3xl"
                  weight="bold"
                >
                  Statements & Documents
                </Heading>
              </Box>
            )}
          </>
        )}
        <TabContext value={currentTab}>
          <TabBoxStyled>
            <TabListStyled onChange={handleTabChange}>
              <TabStyled
                label="Monthly Statements"
                value={STATEMENTS_TAB}
              />
              <TabStyled
                label="Other Documents"
                value={OTHER_DOCUMENTS_TAB}
              />
            </TabListStyled>
          </TabBoxStyled>
          <TabPanel value={STATEMENTS_TAB}>
            {isLoadingSkeleton ? (
              <StatementLoading isLoadingCircle={isLoadingCircle} />
            ) : (
              <MonthlyStatementsTab
                handleLoadMore={handleLoadMore}
                hasNextPagination={hasNextPagination}
                isEmpty={isEmpty}
                loading={loadingStatements}
                yearStatements={yearStatements}
              />
            )}
          </TabPanel>
          <TabPanel value={OTHER_DOCUMENTS_TAB}>
            <OtherDocumentsTab />
          </TabPanel>
        </TabContext>
      </Box>
    </DialogContentStyled>
  );
}

StatementContent.propTypes = {
  handleClose: PropTypes.func,
  openOnPage: PropTypes.bool
};
